import { AdminAbility } from '@repo/sdk/backoffice-abilities'
import type {
	BackofficeAdmin,
	BackofficeAdminStatus,
	BackofficeAuthListAdminsData
} from '@repo/sdk/gen'
import { Avatar, AvatarFallback } from '@repo/ui/avatar'
import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import { ListInputSearch } from '@repo/ui/commons/list-input-search'
import { ListLayout } from '@repo/ui/commons/list-layout'
import { formatLocaleDate } from '@repo/ui/dates'
import { getInitials } from '@repo/ui/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { Users } from 'lucide-react'
import { z } from 'zod'
import { pageTitle, type TKey, translateApp, useTranslation } from '@/lib/i18n'
import { requireAdminPermission } from '@/modules/permissions/lib/require-admin-permission'
import { AbilitiesCell } from '@/routes/(private)/admins/-components/abilities-cell'
import { AdminActionsDropdown } from '@/routes/(private)/admins/-components/admin-actions-dropdown'
import { AdminsFilters } from '@/routes/(private)/admins/-components/admins-filters'
import { InviteAdminDialog } from '@/routes/(private)/admins/-components/invite-admin-dialog'
import { ADMIN_ABILITIES } from '@/routes/(private)/admins/-lib/form-schemas'
import { adminsListQueryOptions } from '@/routes/(private)/admins/-lib/query-options'
import { usePageSize } from '@/stores/use-page-size'

type SortBody = NonNullable<BackofficeAuthListAdminsData['body']['sort']>
type SortBy = NonNullable<SortBody['by']>
type SortDirection = NonNullable<SortBody['direction']>

const SORT_BY_VALUES: readonly SortBy[] = ['createdAt', 'name'] as const
const SORT_DIRECTION_VALUES: readonly SortDirection[] = ['asc', 'desc'] as const
const EMPTY_ADMINS: BackofficeAdmin[] = []

const STATUS_LABEL_KEYS = {
	pending: 'admin-admins:status.pending',
	active: 'admin-admins:status.active',
	disabled: 'admin-admins:status.disabled'
} as const satisfies Record<BackofficeAdminStatus, TKey>

const STATUS_BADGE_VARIANTS = {
	pending: 'outline',
	active: 'secondary',
	disabled: 'destructive'
} as const satisfies Record<BackofficeAdminStatus, string>

// A value the URL cannot hold (an old link, a hand-edited address) falls back
// to its default instead of failing the page.
const searchSchema = z.object({
	abilities: z.array(z.enum(ADMIN_ABILITIES)).optional().catch(undefined),
	search: z.string().trim().default('').catch(''),
	by: z.enum(SORT_BY_VALUES).optional().catch(undefined),
	direction: z.enum(SORT_DIRECTION_VALUES).optional().catch(undefined),
	page: z.number().int().min(1).default(1).catch(1)
})

export const Route = createFileRoute('/(private)/admins')({
	beforeLoad: requireAdminPermission([AdminAbility.AdminsManage]),
	component: AdminAdmins,
	staticData: {
		breadcrumb: 'admin-admins:page_title'
	},
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	},
	head: () => ({
		meta: [
			{ title: pageTitle(translateApp('it', 'admin-admins:page_title')) }
		]
	})
})

function AdminAdmins() {
	const { t } = useTranslation()
	const navigate = Route.useNavigate()
	const params = Route.useSearch()
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const columns: DataTableColumn<BackofficeAdmin, SortBy>[] = [
		{
			id: 'name',
			hideable: false,
			header: t('common:name'),
			pinned: 'left',
			sortKey: 'name',
			cell: (dataItem) => (
				<div className='flex items-center gap-2'>
					<Avatar size='sm'>
						<AvatarFallback>
							{getInitials(dataItem.name ?? '')}
						</AvatarFallback>
					</Avatar>
					<span className='truncate'>{dataItem.name}</span>
				</div>
			),
			minWidth: 140,
			flex: 1
		},
		{
			id: 'email',
			hideable: false,
			header: t('common:email'),
			cell: (dataItem) => dataItem.email,
			minWidth: 180,
			flex: 2
		},
		{
			id: 'status',
			hideable: false,
			header: t('admin-admins:columns.status'),
			cell: (dataItem) => (
				<Badge variant={STATUS_BADGE_VARIANTS[dataItem.status]}>
					{t(STATUS_LABEL_KEYS[dataItem.status])}
				</Badge>
			),
			minWidth: 110,
			flex: 1
		},
		{
			id: 'abilities',
			hideable: false,
			header: t('admin-admins:columns.abilities'),
			cell: (dataItem) => (
				<AbilitiesCell abilities={dataItem.abilities ?? []} />
			),
			minWidth: 160,
			flex: 2
		},
		{
			id: 'createdAt',
			hideable: false,
			header: t('common:created_at'),
			sortKey: 'createdAt',
			cell: (dataItem) =>
				formatLocaleDate(dataItem.createdAt, 'P HH:mm:ss'),
			minWidth: 160,
			flex: 1
		},
		{
			id: 'id',
			hideable: false,
			header: t('admin-admins:columns.id'),
			cell: (dataItem) => dataItem.id,
			minWidth: 220,
			flex: 1
		},
		{
			id: 'actions',
			header: t('admin-admins:columns.actions'),
			pinned: 'right',
			hideable: false,
			manageable: false,
			pinnable: false,
			enableResizing: false,
			cell: (dataItem) => (
				<div className='flex w-full justify-end'>
					<AdminActionsDropdown admin={dataItem} />
				</div>
			),
			width: 88,
			minWidth: 88,
			maxWidth: 88
		}
	]

	const filters = {
		search: params.search || undefined,
		abilities: params.abilities?.length ? params.abilities : undefined
	}
	const sort =
		params.by === undefined
			? undefined
			: { by: params.by, direction: params.direction ?? 'asc' }

	const query = useQuery(
		adminsListQueryOptions({
			page: params.page,
			limit: pageSize,
			filters,
			sort
		})
	)

	return (
		<ListLayout
			controlsSlot={
				<>
					<ListInputSearch
						placeholder={t('admin-admins:search_placeholder')}
						resetKeys={['page']}
					/>
					<AdminsFilters />
				</>
			}
			headEndSlot={
				<div className='ml-auto'>
					<InviteAdminDialog />
				</div>
			}
			listSlot={
				<DataTable
					columns={columns}
					data={query.data?.data ?? EMPTY_ADMINS}
					pagination={{
						pageIndex: params.page - 1,
						pageCount: query.data?.meta.totalPages ?? 1,
						onPageChange: (next) =>
							navigate({
								search: (prev) => ({ ...prev, page: next + 1 }),
								replace: true
							}),
						pageSize,
						onPageSizeChange: (next) => {
							setPageSize(next)
							navigate({
								search: (prev) => ({ ...prev, page: 1 }),
								replace: true
							})
						}
					}}
					sort={{
						state: { by: params.by, direction: params.direction },
						onSortChange: (next) =>
							navigate({
								search: (prev) => ({
									...prev,
									by: next.by,
									direction: next.direction,
									page: 1
								}),
								replace: true
							})
					}}
					state={{
						loading: query.isPending || query.isPlaceholderData,
						error: query.isError,
						empty: {
							icon: <Users />,
							title: t('admin-admins:empty_title'),
							description: t('admin-admins:empty_description')
						}
					}}
				/>
			}
			title={t('admin-admins:page_title')}
		/>
	)
}
